/**
 * Axios 实例配置
 * 配置请求拦截器和响应拦截器
 */

import axios from 'axios';
import { getToken, setToken, removeToken } from './auth';
import { refreshToken } from '../api/auth';

// 创建 Axios 实例
const instance = axios.create({
    timeout: 120000,
    headers: {
        'Content-Type': 'application/json'
    }
});

// 是否正在刷新 Token
let isRefreshing = false;
// 等待刷新 Token 的请求队列
let refreshSubscribers = [];

/**
 * 订阅 Token 刷新
 * @param {function} callback - 回调函数
 */
function subscribeTokenRefresh(callback) {
    refreshSubscribers.push(callback);
}

/**
 * 通知所有订阅者 Token 已刷新
 * @param {string} token - 新 Token
 */
function onTokenRefreshed(token) {
    refreshSubscribers.forEach(callback => callback(token));
    refreshSubscribers = [];
}

/**
 * 处理 Token 刷新失败
 */
function onRefreshTokenFailed() {
    refreshSubscribers = [];
    removeToken();
    // 跳转到登录页
    window.location.href = '/userLogin';
}

// 请求拦截器
instance.interceptors.request.use(
    (config) => {
        const token = getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// 响应拦截器
instance.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // 如果是 401 错误且不是刷新 Token 的请求
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            // 如果正在刷新 Token，将请求加入队列
            if (isRefreshing) {
                return new Promise((resolve) => {
                    subscribeTokenRefresh((token) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        resolve(instance(originalRequest));
                    });
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            const token = getToken();
            if (token) {
                try {
                    // 尝试刷新 Token
                    const result = await refreshToken(token);

                    if (result.success && result.token) {
                        setToken(result.token);
                        onTokenRefreshed(result.token);

                        originalRequest.headers.Authorization = `Bearer ${result.token}`;
                        return instance(originalRequest);
                    } else {
                        onRefreshTokenFailed();
                        return Promise.reject(error);
                    }
                } catch (e) {
                    onRefreshTokenFailed();
                    return Promise.reject(e);
                } finally {
                    isRefreshing = false;
                }
            } else {
                onRefreshTokenFailed();
                return Promise.reject(error);
            }
        }

        return Promise.reject(error);
    }
);

export default instance;
